// RGR01 반품입고 저장·삭제·확정·엑셀 다운로드 — 2.0 API 전용(1.0 /api/grid/save·/api/internal/data/send 를 부르지 않는다).
import { useMutation, UseMutationResult } from '@tanstack/react-query'
import request, { getExcel } from '@/utils/request'
import { menuCodeHeaders } from '@/utils/menuCodeHeaders'
import { ReturnArrivalNoticeItem, ReturnInboundParams, ReturnInboundRow, RETURN_ORD_TYPE, toSearchFilter } from './useReturnInboundList'

/** 저장 전 필수 컬럼 — 화면이 생성본 COLUMNS 의 `required` 에서 파생해 넘긴다(표의 별표와 검사가 갈라지지 않게) */
export interface RequiredField {
  key: keyof ReturnInboundRow
  title: string
}

/** 삭제 요청 상한 — BE DeleteArrivalNoticesRequest `@Size(max = 1000)` */
export const DELETE_IDS_MAX = 1000

const blank = (v: unknown): string | null => {
  const s = v == null ? '' : String(v).trim()
  return s || null
}

/**
 * 저장에 쓰는 화주유형 — 저장된 행은 조회 원본, 업로드 신규 행만 셀 값이다. BE 는 요청 serviceType 으로 최종귀책 저장 여부와
 * MFS 반품 companyName 을 정하고(InboundRequestMapperImpl:121,145-152) 수정 경로가 그 결과로 덮어쓴다. 행의 화주는 바뀌지
 * 않으므로(update 가 shipperCode 를 두지 않는다) 편집값을 보내면 원장 최종귀책·회사명만 잘못 바뀐다(화주코드 saveShipper 와 같은 규칙).
 */
const serviceTypeOf = (row: ReturnInboundRow): string => (row._src ? (row._src.serviceType ?? '') : row.service_type)

/**
 * 저장 요청의 화주 — 저장된 행은 조회 원본, 업로드 신규 행만 셀 값이다. BE 는 수정 경로에서 행의 화주를 바꾸지 않으면서
 * 요청 shipper 로 바코드→brandId 를 조회해 덮어쓴다(InboundRequestMapperImpl) — 편집값을 보내면 원장 brandId 가 지워진다.
 * 화주유형 규칙(`serviceTypeOf`)과 짝이다.
 */
export const saveShipper = (row: ReturnInboundRow): string => (row._isNew ? row.SPR_NM : (row._src?.sprNm ?? ''))

/** 날짜 → 'yyyyMMdd' (BE @JsonFormat). 문자열·[y,M,d] 배열 모두 받는다. 못 읽으면 null */
export const toYmd = (v: unknown): string | null => {
  if (Array.isArray(v)) {
    const [y, m, d] = v as number[]
    return y && m && d ? `${y}${String(m).padStart(2, '0')}${String(d).padStart(2, '0')}` : null
  }
  const digits = String(v ?? '').replace(/[^0-9]/g, '')
  return digits.length >= 8 ? digits.slice(0, 8) : null
}

/** request-inbound skuId 길이 상한 — BE ItemRequestBody.skuId 의 @Size(max = 14). 넘으면 요청 전체가 400 이고 어느 행인지 알려 주지 않는다 */
export const SKU_ID_MAX = 14

/**
 * 저장된 행에서 사용자가 SKUID 칸을 고친 경우에만 skuId 를 싣는다. BE 는 skuId 가 비면 저장분을 그대로 두므로
 * (ArrivalNoticeService.update) 싣지 않으면 고친 값이 저장되지 않는다.
 *
 * 그 밖의 행은 싣지 않는다. 신규 행에 값을 실으면 `applyResolvedSkuIds`(InboundController)가 바코드로 해소한 SKU 를
 * 버린다. 바꾸지 않은 행은 실어도 안 실어도 BE 가 저장분을 유지하므로 요청을 줄이려고 뺀다. 칸을 비운 행도
 * 싣지 않는다 — 빈 값은 BE 가 "변경 없음" 으로 읽어 저장분이 남는다.
 *
 * 저장된 행에서 바코드만 바꾸면 `followBarcode` 가 SKUID 칸을 비우고, BE 는 저장된 행의 SKU 를 바코드로 다시 해소하지
 * 않는다. 그래서 옛 SKU 가 새 바코드에 남는다. 이 경우 SKUID 를 직접 고쳐야 한다.
 */
export const editedSkuId = (row: ReturnInboundRow): { skuId?: string } => {
  if (row._isNew || !row._src) return {}
  const edited = row.sku_id.trim()
  if (!edited || edited === (row._src.skuId ?? '').trim()) return {}
  return { skuId: edited }
}

/**
 * 표 행 → request-inbound items[] 1건. 필드 대응은 1.0 InboundUtil.FIELD_MAP 과 같다.
 *
 * 화면에 없는 필드는 조회 원본(`_src`)에서 되돌려 보낸다. BE 는 기존 행을 고칠 때 요청에 없는 필드를
 * null 로 덮어써서(ArrivalNoticeService.update), 안 보내면 저장 한 번에 원장 값이 지워진다.
 */
export const toItemBody = (row: ReturnInboundRow) => {
  const s: Partial<ReturnArrivalNoticeItem> = row._src ?? {}
  return {
    ...(row._isNew ? {} : { id: row._id }),
    plannedDate: toYmd(row.PLN_DATE),
    // 1.0 InboundUtil 이 RGR01 은 '반품' 으로 고정한다. '일반' 이 아니면 BE 가 RGR01 로 저장한다
    orderType: RETURN_ORD_TYPE,
    orderNumber: s.ordNo ?? null,
    orderOptionNumber: blank(row.ORD_OPT_NO),
    warehouseName: blank(row.WH_NM),
    warehouseArea: s.whArea ?? null,
    salesChannel: blank(row.SAL_CHANNEL),
    styleNumber: blank(row.STL_NO),
    barcode: row.BARCODE.trim(),
    ...editedSkuId(row),
    quantity: Number(row.ORD_QTY),
    brandName: blank(row.BRD_NM),
    purchasePrice: s.apPrice ?? 0,
    apiFlag: s.apiFlag ?? false,
    flag: s.flag ?? false,
    interfaceRequired: s.ifReq ?? true,
    procurementName: blank(row.PROC_NM),
    uid: blank(row.UID),
    incoterms: s.incoterms ?? null,
    advancedShippingNoticeSequence: s.asnSeq ?? null,
    companyName: s.comNm ?? null,
    optionCode: blank(row.option_code) ? Number(row.option_code) : null,
    // 한글 라벨 그대로 — BE FinalResponsibility @JsonCreator 가 displayName 으로 되돌린다. MFS 만 저장된다
    finalResponsibility: blank(row.final_responsibility),
    serviceType: blank(serviceTypeOf(row)),
    manufacturedDate: toYmd(s.manufacturedDate),
    expirationDate: toYmd(s.expirationDate),
    goods: {
      goodsCode: s.gdsCd ?? null,
      goodsGroup: blank(row.GDS_GROUP1),
      goodsName: row.GDS_NM.trim(),
      goodsOption: blank(row.GDS_OPT),
      goodsType: s.gdsAttr1 ?? null,
      goodsPrice: s.apPrice ?? 0,
      goodsColor: s.gdsColor ?? null,
    },
    transfer: {
      invoiceNumber: blank(row.TRS_INVOICE_NO),
      logisticsName: blank(row.TRS_LSP_NM),
      transferType: blank(row.TRS_TYPE),
      transferName: blank(row.TRS_NM),
      transferNumber: blank(row.TRS_NUMBER),
      transferPhone: blank(row.TRS_PHONE),
      shippingSourceLocation: blank(row.TRS_ADDR),
      transferMessage: s.trsMsg ?? null,
    },
    return: {
      returnChannel: s.rlChannel ?? null,
      returnType: s.rtnType ?? null,
      reasonCode: s.rsnCode ?? null,
      jobCommentForInvoice: s.jobCommentForInvoice ?? null,
      claimId: blank(row.claim_id),
    },
    ref: { ref1: blank(row.REF_NO1), ref2: blank(row.REF_NO2), ref3: blank(row.REF_NO3), ref4: s.refNo4 ?? null, ref5: s.refNo5 ?? null },
    // 온라인·양품은 원장에 빈 값이다(1.0 RGR01 onClickChange 도 null 로 보냈다)
    lot: {
      lot1: s.lotAttr1 ?? null,
      lot2: s.lotAttr2 ?? null,
      lot3: s.lotAttr3 ?? null,
      lot4: blank(row.LOT_ATTR4),
      lot5: blank(row.LOT_ATTR5),
      lot6: s.lotAttr6 ?? null,
    },
    etc: {
      etc1: blank(row.ETC1),
      etc2: blank(row.ETC2),
      etc3: blank(row.ETC3),
      etc4: blank(row.ETC4),
      etc5: blank(row.ETC5),
      etc6: blank(row.ETC6),
    },
  }
}

const isBlank = (value: unknown) => value == null || String(value).trim() === ''

/** 받침 유무에 따라 주격조사를 고른다 — '상품명이', '바코드가' */
const subjectParticle = (word: string) => {
  const last = word.charCodeAt(word.length - 1)
  if (last < 0xac00 || last > 0xd7a3) return '이(가)'
  return (last - 0xac00) % 28 === 0 ? '가' : '이'
}

/** 저장 전 필수값 검사 — BE 400 이 나기 전에 행을 짚어준다 */
export const validateRows = (rows: ReturnInboundRow[], requiredFields: RequiredField[]): string[] =>
  rows.reduce<string[]>((acc, row, index) => {
    const label = row.claim_id.trim() || row.BARCODE.trim() || `${index + 1}번째 행`
    requiredFields.forEach(({ key, title: field }) => {
      // 최종귀책은 BE 가 MFS 행만 저장한다(InboundRequestMapperImpl) — 다른 행은 저장해도 비워지므로 검사하지 않는다(2026-09-16 결정)
      if (key === 'final_responsibility' && serviceTypeOf(row) !== 'MFS') return
      if (isBlank(row[key])) acc.push(`${label}: ${field}${subjectParticle(field)} 비어 있습니다.`)
    })
    if (!isBlank(row.ORD_QTY) && Number.isNaN(Number(row.ORD_QTY))) acc.push(`${label}: 수량이 숫자가 아닙니다.`)
    if (!isBlank(row.option_code) && Number.isNaN(Number(row.option_code))) acc.push(`${label}: 옵션코드가 숫자가 아닙니다.`)
    if ((editedSkuId(row).skuId?.length ?? 0) > SKU_ID_MAX) acc.push(`${label}: SKUID는 최대 ${SKU_ID_MAX}자입니다.`)
    return acc
  }, [])

/**
 * 화주 혼재 판정 — 섞여 있으면 화주코드 목록, 아니면 빈 배열.
 * 저장·삭제·확정 요청 본문이 화주 단건이라 섞이면 막는다(확정은 BE 도 "화주사 코드는 모두 동일해야" 로 거절한다).
 */
export const mixedShippers = (codes: string[]): string[] => {
  const unique = [...new Set(codes)]
  return unique.length > 1 ? unique : []
}

/** 저장 — POST /api/v1/request-inbound. 기존 행은 items[].id 로 갱신, 업로드 신규 행은 id 없이 생성 */
export const useSaveReturnInbounds = (): UseMutationResult<number, Error, { shipper: string; rows: ReturnInboundRow[] }> =>
  useMutation({
    mutationFn: async ({ shipper, rows }) => {
      await request.post<ApiResponseV2<unknown>, { shipper: string; items: ReturnType<typeof toItemBody>[] }>(
        '/api/v1/request-inbound',
        { shipper, items: rows.map(toItemBody) },
        // request-inbound 는 gr07 과 rgr16 이 같이 부른다 — 감사 로그 화면코드를 헤더로 보낸다(LOGISPRD-15996)
        menuCodeHeaders('RGR16'),
      )
      return rows.length
    },
  })

/** 삭제 — POST /api/v1/return/inbounds/delete. BE 는 SPR_NM·MENU_CD(RGR01) 가 맞는 행만 지우고 지운 건수를 준다 */
export const useDeleteReturnInbounds = (): UseMutationResult<number, Error, { shipperCode: string; ids: number[] }> =>
  useMutation({
    mutationFn: async (body) => {
      const res = await request.post<ApiResponseV2<{ affectedRows: number }>, { shipperCode: string; ids: number[] }>(
        '/api/v1/return/inbounds/delete',
        body,
      )
      return res.data?.affectedRows ?? 0
    },
  })

/**
 * 확정 — POST /api/v1/return/confirm-inbound. 선택 행만 보낸다.
 * ids 를 비우면 BE 가 그 화주의 미확정 반품 전건을 확정한다(NormalInboundPlanService) — 데모 [확정]은 선택 0건이면
 * 비활성이라 그 경로를 쓰지 않는다(LOGISPRD-12753 결정 9).
 */
export const useConfirmReturnInbounds = (): UseMutationResult<number, Error, { shipper: string; ids: number[] }> =>
  useMutation({
    mutationFn: async (body) => {
      if (!body.ids.length) throw new Error('확정할 행을 선택하세요.')
      await request.post<ApiResponseV2<{ items: number[] }>, { shipper: string; ids: number[] }>('/api/v1/return/confirm-inbound', body)
      return body.ids.length
    },
  })

export interface DownloadReason {
  reasonCode: string
  detailedReason: string
  password: string
}

/**
 * 엑셀 다운로드 — POST /api/v1/return/inbounds/excel. 조회와 같은 필터 + 사유·비밀번호.
 * 응답은 암호 걸린 zip 바이트다(BE ExcelZipper). 오류는 JSON 본문으로 온다.
 */
export const downloadReturnInboundsExcel = async (params: ReturnInboundParams, reason: DownloadReason): Promise<void> => {
  const res = await getExcel('/api/v1/return/inbounds/excel', { method: 'post', data: { ...toSearchFilter(params), ...reason } })
  const disposition = String(res.headers?.['content-disposition'] ?? '')
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)/i.exec(disposition)
  const fileName = match ? decodeURIComponent(match[1]) : '반품입고.zip'
  const anchor = document.createElement('a')
  anchor.href = URL.createObjectURL(res.data)
  anchor.download = fileName
  anchor.click()
  // click() 은 다운로드를 예약만 한다 — 같은 틱에 해제하면 Firefox 에서 빈 파일이 된다(GR01 준용)
  setTimeout(() => URL.revokeObjectURL(anchor.href), 0)
}
